import { Assets, Sprite, Texture } from "pixi.js";

export const parseJwt = (token) => {
  if (!token) {
    return {};
  }
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

export const getRandomItemFromArray = (array) => {
  const randomIndex = Math.floor(Math.random() * array.length);
  return array[randomIndex];
};

export const getRandomItemAndIndexFromArray = (arr) => {
  if (arr.length === 0) return null;
  const index = Math.floor(Math.random() * arr.length);
  return { item: arr[index], index };
};

export const getRandomBetween = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const getRandomFloatBetween = (min, max) => {
  return min + Math.random() * (max - min);
};

export const isLocalStorageAvailable = () => {
  try {
    localStorage.getItem("isSoundOff");
    return true;
  } catch (error) {
    return false;
  }
};

export const hexToRgb = (hex) => {
  if (!hex) {
    return hex;
  }
  hex = hex.replace(/^#/, "");
  let bigint = parseInt(hex, 16);

  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;

  return `rgb(${r}, ${g}, ${b})`;
};

export const hexToRgbRaw = (hex) => {
  if (!hex) return "0, 230, 138";
  hex = hex.replace(/^#/, "");
  const bigint = parseInt(hex, 16);
  return `${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}`;
};

export function mapRange(input, inputMin, inputMax, outputMin, outputMax) {
  input = Math.max(inputMin, Math.min(input, inputMax));
  return outputMin + (outputMax - outputMin) * ((input - inputMin) / (inputMax - inputMin));
}

export function hasMoreThanTwoDecimals(number) {
  const str = number.toString();
  const parts = str.split(".");
  return parts.length === 2 && parts[1].length > 2;
}

export function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function changeStringifySvgColors(svgString, mappings) {
  let modified = svgString;

  for (const { color, newColor } of mappings) {
    const pattern = new RegExp(escapeRegExp(color), "g");
    modified = modified.replace(pattern, newColor);
  }

  return modified;
}

const svgTextureCache = new Map();

export const spriteFromStringifySvg = (stringifySvg) => {
  let texture = svgTextureCache.get(stringifySvg);
  if (!texture) {
    const blob = new Blob([stringifySvg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    texture = Texture.from(url);
    svgTextureCache.set(stringifySvg, texture);
  }

  return new Sprite(texture);
};

export function adjustHexTone(hex, percent) {
  // 1) Normalize and strip “#”
  let normalized = hex.replace(/^#/, "");
  if (normalized.length === 3) {
    // expand shorthand (“#ABC” → “#AABBCC”)
    normalized = normalized
      .split("")
      .map((c) => c + c)
      .join("");
  }

  // 2) Parse R, G, B
  const num = parseInt(normalized, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  // 3) Calculate new channel values
  const applyShade = (channel) => {
    if (percent < 0) {
      // Darken: multiply by (1 + percent), since percent is negative
      return Math.round(channel * (1 + percent));
    } else {
      // Lighten: add (255 - channel) * percent
      return Math.round(channel + (255 - channel) * percent);
    }
  };

  r = applyShade(r);
  g = applyShade(g);
  b = applyShade(b);

  // 4) Ensure 0–255 and convert back to two-digit hex
  const toHex = (val) => {
    const h = Math.max(0, Math.min(255, val)).toString(16);
    return h.length === 1 ? "0" + h : h;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export const generateCrashPoint = (houseEdge = 0.001) => {
  const factor = 1 / houseEdge;
  const u = Math.random();
  const raw = (factor - u) / (1 - u);
  return Math.floor(raw) / factor;
};

export const findClosest = (numbersArr, myNum) => {
  if (numbersArr.length === 0) return undefined;
  return numbersArr.reduce((prev, curr) => (Math.abs(curr - myNum) < Math.abs(prev - myNum) ? curr : prev));
};

export const stringToJsObj = (str) => {
  const withQuotedKeys = str.replace(/([{,]\s*)([A-Za-z0-9_]+)\s*:/g, '$1"$2":');
  const withDoubles = withQuotedKeys.replace(/'/g, '"');
  const cleanJSON = withDoubles.replace(/,\s*([}\]])/g, "$1");
  return JSON.parse(cleanJSON);
};

export function formatKm(n, opts = {}) {
  const { decimals = 2, trimZeros = true, round = "floor" } = opts;

  if (!Number.isFinite(n)) return String(n);

  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  const units = [
    { value: 1e6, suffix: "m" },
    { value: 1e3, suffix: "k" },
  ];

  const unit = units.find((u) => abs >= u.value);
  if (!unit) {
    // below 1000: return plain number (optionally with decimals)
    let s = decimals > 0 ? abs.toFixed(decimals) : String(abs);
    if (trimZeros && s.includes(".")) s = s.replace(/\.?0+$/, "");
    return sign + s;
  }

  const scale = abs / unit.value;
  const factor = Math.pow(10, Math.max(0, decimals));
  const rounder = round === "ceil" ? Math.ceil : round === "round" ? Math.round : Math.floor;

  let val = rounder(scale * factor) / factor;

  // If rounding pushed us to the next unit (only happens with 'round'/'ceil'), bump once.
  if (val >= 1000 && (round === "round" || round === "ceil")) {
    const nextIndex = units.findIndex((u) => u.value === unit.value) - 1;
    if (nextIndex >= 0) {
      const next = units[nextIndex];
      val = rounder((abs / next.value) * factor) / factor;
      return sign + (trimZeros ? trim(val, decimals) : val.toFixed(decimals)) + next.suffix;
    }
  }

  return sign + (trimZeros ? trim(val, decimals) : val.toFixed(decimals)) + unit.suffix;

  function trim(v, d) {
    const s = d > 0 ? v.toFixed(d) : String(v);
    return s.includes(".") ? s.replace(/\.?0+$/, "") : s;
  }
}

export const clsx = (...args) => {
  return args.filter(Boolean).join(" ");
};

export function createDeferred() {
  let resolve;
  let reject;

  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { promise, resolve, reject };
}

export const linerInterpolate = (start, end, t, clamp = true) => {
  if (clamp) t = Math.min(1, Math.max(0, t));
  return start + (end - start) * t;
};

export const triangleInterpolate = (start, end, t, clamp = true) => {
  if (clamp) t = Math.min(1, Math.max(0, t));
  const u = 1 - Math.abs(2 * t - 1);
  return start + (end - start) * u;
};

export const interpolate2D = (a, b, t) => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

const textures = {};
export const getAsset = (key) => {
  const memoizedTexture = textures[key];
  if (memoizedTexture) return memoizedTexture;

  const texture = Assets.get(key);
  textures[key] = texture;
  return texture;
};

export const createLinearGradientTexture = (width, height, colorStops, vertical = true) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Texture.EMPTY;
  const [x0, y0] = vertical ? [0, 0] : [0, 0];
  const [x1, y1] = vertical ? [0, height] : [width, 0];
  const grad = ctx.createLinearGradient(x0, y0, x1, y1);
  colorStops.forEach(({ offset, color }) => {
    grad.addColorStop(offset, color);
  });
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);
  return Texture.from(canvas);
};

const getOriginDomain = () => {
  const splitted = window.location.origin.split(".");
  const origin = [splitted[splitted.length - 2], splitted[splitted.length - 1]].join(".");
  return origin;
};

export const getGameOriginDomain = () => {
  if (process.env.REACT_APP_ENVIRONMENT === "Development") return process.env.REACT_APP_DEVELOPMENT_ORIGIN_GAME;
  return getOriginDomain();
};

export const getIntegrationsOriginDomain = () => {
  if (["Development", "Staging"].includes(process.env.REACT_APP_ENVIRONMENT))
    return process.env.REACT_APP_DEVELOPMENT_ORIGIN_MAIN;
  return getOriginDomain();
};

export const getTranslationsOriginDomain = () => {
  if (["Development", "Staging"].includes(process.env.REACT_APP_ENVIRONMENT))
    return process.env.REACT_APP_DEVELOPMENT_ORIGIN_MAIN;
  return getOriginDomain();
};
